/**
 * KnowledgeBase - Manage AI memories from Supabase
 *
 * Displays paginated memories stored for the user with:
 * - Search functionality
 * - Add new memory manually
 * - Delete memory
 * - Pagination controls
 */

import { useState, useEffect, useCallback } from "react";
import { Card, Spinner, Alert, Button, Form, Pagination } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import AddMemoryModal from "./common/AddMemoryModal";
import EditMemoryModal from "./common/EditMemoryModal";
import ConfirmationPopup from "./common/ConfirmationPopup";
import "../assets/css/knowledge-base.css";

// Category configuration with icons and colors
const CATEGORY_CONFIG = {
  identity: { icon: "👤", label: "Identity", color: "#6366f1" },
  preference: { icon: "❤️", label: "Preference", color: "#f97316" },
  personality: { icon: "🎭", label: "Personality", color: "#8b5cf6" },
  health: { icon: "🏥", label: "Health", color: "#ef4444" },
  finance: { icon: "💰", label: "Finance", color: "#22c55e" },
  work: { icon: "💼", label: "Work", color: "#3b82f6" },
  education: { icon: "🎓", label: "Education", color: "#0ea5e9" },
  relationship: { icon: "👨‍👩‍👧", label: "Relationship", color: "#ec4899" },
  food: { icon: "🍽️", label: "Food", color: "#14b8a6" },
  travel: { icon: "✈️", label: "Travel", color: "#06b6d4" },
  hobby: { icon: "🎮", label: "Hobby", color: "#a855f7" },
  schedule: { icon: "📅", label: "Schedule", color: "#8b5cf6" },
  goal: { icon: "🎯", label: "Goal", color: "#eab308" },
  event: { icon: "📆", label: "Event", color: "#f59e0b" },
  general: { icon: "📝", label: "General", color: "#6b7280" },
};

// Helper: Truncate text
const truncateText = (text, maxLength = 150) => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "...";
};

// Category Badge Component
const CategoryBadge = ({ category }) => {
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.general;
  return (
    <span 
      className="kb-category-badge" 
      style={{ backgroundColor: config.color }}
    >
      {config.icon} {config.label}
    </span>
  );
};

// Importance Indicator Component
const ImportanceIndicator = ({ importance }) => {
  if (importance < 0.7) return null; // Only show for high importance
  
  const level = importance >= 0.9 ? "critical" : "high";
  const stars = importance >= 0.9 ? 3 : 2;
  const color = importance >= 0.9 ? "#ef4444" : "#f97316";
  
  return (
    <span className="kb-importance-indicator" title={`${level} importance`}>
      {Array(stars).fill().map((_, i) => (
        <i key={i} className="bi bi-star-fill" style={{ color }}></i>
      ))}
    </span>
  );
};

const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || "http://localhost:3000";
const PAGE_SIZE = 10;

export default function KnowledgeBase({ userId }) {
  const [loading, setLoading] = useState(true);
  const [memories, setMemories] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [memoryToEdit, setMemoryToEdit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [memoryToDelete, setMemoryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Multi-select state
  const [selectedMemories, setSelectedMemories] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  // Expanded memories state
  const [expandedMemories, setExpandedMemories] = useState({});

  // Toggle expand/collapse
  const toggleExpanded = (memoryId) => {
    setExpandedMemories((prev) => ({
      ...prev,
      [memoryId]: !prev[memoryId]
    }));
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Load memories
  const loadMemories = useCallback(async (page = 1) => {
    if (!userId) return;

    try {
      setLoading(true);
      const queryParam = searchQuery ? `&query=${encodeURIComponent(searchQuery)}` : "";
      const categoryParam = categoryFilter !== "all" ? `&category=${categoryFilter}` : "";
      const response = await fetch(
        `${API_URL}/api/knowledge-base?user_id=${userId}&page=${page}&page_size=${PAGE_SIZE}${queryParam}${categoryParam}`
      );
      const data = await response.json();

      if (data.memories) {
        setMemories(data.memories);
        if (data.pagination) {
          setCurrentPage(data.pagination.page);
          setTotalPages(data.pagination.total_pages);
          setTotalItems(data.pagination.total);
        }
      } else if (data.error) {
        setError(data.error);
      }
    } catch (err) {
      console.error("[KnowledgeBase] Error loading:", err);
      setError("Failed to load memories");
    } finally {
      setLoading(false);
    }
  }, [userId, searchQuery, categoryFilter]);

  useEffect(() => {
    loadMemories(1);
  }, [userId, categoryFilter]); // Reload on userId or category change

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadMemories(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadMemories(page);
  };

  // Handle add memory
  const handleAddMemory = async (content) => {
    try {
      const response = await fetch(`${API_URL}/api/knowledge-base`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "add",
          user_id: userId,
          content,
          metadata: { source: "manual" },
        }),
      });

      const data = await response.json();
      if (data.success) {
        setShowAddModal(false);
        loadMemories(1); // Go back to first page after adding
      } else {
        setError(data.error || "Failed to add memory");
      }
    } catch (err) {
      console.error("[KnowledgeBase] Error adding:", err);
      setError("Failed to add memory");
    }
  };

  // Handle edit
  const handleEditClick = (memory) => {
    setMemoryToEdit(memory);
    setShowEditModal(true);
  };

  // Handle edit save
  const handleEditSave = async (memoryId, content, category, importance) => {
    try {
      const response = await fetch(`${API_URL}/api/knowledge-base`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          memory_id: memoryId,
          content,
          category,
          importance,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setShowEditModal(false);
        setMemoryToEdit(null);
        loadMemories(currentPage);
      } else {
        setError(data.error || "Failed to update memory");
      }
    } catch (err) {
      console.error("[KnowledgeBase] Error updating:", err);
      setError("Failed to update memory");
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (memory) => {
    setMemoryToDelete(memory);
    setShowDeleteConfirm(true);
  };

  // Handle delete
  const handleDelete = async () => {
    // Bulk delete
    if (selectedMemories.length > 0) {
      try {
        setDeleting(true);
        const response = await fetch(`${API_URL}/api/knowledge-base`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            memory_ids: selectedMemories,
          }),
        });

        const data = await response.json();
        if (data.success) {
          setShowDeleteConfirm(false);
          setSelectedMemories([]);
          setIsMultiSelectMode(false);
          // If we deleted all items on this page, go to previous page
          if (selectedMemories.length >= memories.length && currentPage > 1) {
            loadMemories(currentPage - 1);
          } else {
            loadMemories(currentPage);
          }
        } else {
          setError(data.error || "Failed to delete memories");
        }
      } catch (err) {
        console.error("[KnowledgeBase] Error bulk deleting:", err);
        setError("Failed to delete memories");
      } finally {
        setDeleting(false);
      }
      return;
    }

    // Single delete
    if (!memoryToDelete) return;

    try {
      setDeleting(true);
      const response = await fetch(`${API_URL}/api/knowledge-base`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          memory_id: memoryToDelete.id,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setShowDeleteConfirm(false);
        setMemoryToDelete(null);
        // If we deleted the last item on a page, go to previous page
        if (memories.length === 1 && currentPage > 1) {
          loadMemories(currentPage - 1);
        } else {
          loadMemories(currentPage);
        }
      } else {
        setError(data.error || "Failed to delete memory");
      }
    } catch (err) {
      console.error("[KnowledgeBase] Error deleting:", err);
      setError("Failed to delete memory");
    } finally {
      setDeleting(false);
    }
  };

  // Multi-select handlers
  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    if (isMultiSelectMode) {
      setSelectedMemories([]);
    }
  };

  const toggleMemorySelection = (memoryId) => {
    setSelectedMemories((prev) =>
      prev.includes(memoryId)
        ? prev.filter((id) => id !== memoryId)
        : [...prev, memoryId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedMemories.length === memories.length) {
      setSelectedMemories([]);
    } else {
      setSelectedMemories(memories.map((memory) => memory.id));
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedMemories.length === 0) return;
    setMemoryToDelete(null);
    setShowDeleteConfirm(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.First
        key="first"
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    if (startPage > 1) {
      items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => handlePageChange(totalPages)}
        disabled={currentPage === totalPages}
      />
    );

    return (
      <div className="kb-pagination-wrapper">
        <span className="kb-pagination-info">
          Showing {((currentPage - 1) * PAGE_SIZE) + 1}-{Math.min(currentPage * PAGE_SIZE, totalItems)} of {totalItems}
        </span>
        <Pagination className="mb-0">{items}</Pagination>
      </div>
    );
  };

  if (loading && memories.length === 0) {
    return (
      <div className="knowledge-base-container">
        <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="knowledge-base-container">
      {/* Stats Bar */}
      <div className="kb-stats-bar">
        <div className="kb-stat-card">
          <div className="kb-stat-icon teal">
            <i className="bi bi-lightbulb-fill"></i>
          </div>
          <div className="kb-stat-info">
            <h3>{totalItems}</h3>
            <p>Total Memories</p>
          </div>
        </div>
        <div className="kb-stat-card">
          <div className="kb-stat-icon orange">
            <i className="bi bi-person-fill"></i>
          </div>
          <div className="kb-stat-info">
            <h3>{memories.filter(m => m.metadata?.source === "manual").length}</h3>
            <p>Manual Entries</p>
          </div>
        </div>
      </div>

      {/* Search & Actions Bar */}
      <div className="kb-actions-bar">
        <div className="kb-search-filter-wrapper">
          <div className="kb-search-wrapper">
            <i className="bi bi-search kb-search-icon-left"></i>
            <Form onSubmit={handleSearch} style={{ display: 'contents' }}>
              <input
                type="text"
                placeholder="Search your memories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Form>
          </div>
          <Form.Select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="kb-category-filter"
          >
            <option value="all">All Types</option>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>
                {config.icon} {config.label}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="kb-action-buttons">
          {isMultiSelectMode ? (
            <>
              <button
                className="kb-select-all-btn"
                onClick={toggleSelectAll}
              >
                <i className={`bi ${selectedMemories.length === memories.length && memories.length > 0 ? 'bi-check-square-fill' : 'bi-square'}`}></i>
                {selectedMemories.length === memories.length && memories.length > 0 ? 'Deselect All' : 'Select All'}
              </button>
              <button
                className="kb-delete-selected-btn"
                onClick={handleBulkDeleteClick}
                disabled={selectedMemories.length === 0}
              >
                <i className="bi bi-trash"></i>
                Delete ({selectedMemories.length})
              </button>
              <button
                className="kb-cancel-btn"
                onClick={toggleMultiSelectMode}
              >
                <i className="bi bi-x-lg"></i>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                className="kb-select-btn"
                onClick={toggleMultiSelectMode}
                disabled={memories.length === 0}
              >
                <i className="bi bi-check2-square"></i>
                Select
              </button>
              <button
                className="kb-add-btn"
                onClick={() => setShowAddModal(true)}
              >
                <i className="bi bi-plus-lg"></i>
                Add Memory
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {/* Memories List */}
      <div className="kb-memories-list">
        {memories.length === 0 ? (
          <div className="kb-empty-state">
            <div className="kb-empty-icon">
              <i className="bi bi-lightbulb"></i>
            </div>
            <h5>No memories yet</h5>
            <p>
              {searchQuery
                ? "No memories match your search"
                : "Add facts about yourself that you want Zoomey to remember."}
            </p>
            {!searchQuery && (
              <Button
                variant="outline-primary"
                onClick={() => setShowAddModal(true)}
              >
                <i className="bi bi-plus-lg me-2"></i>
                Add Your First Memory
              </Button>
            )}
          </div>
        ) : (
          memories.map((memory, index) => (
            <Card
              key={memory.id || index}
              className={`kb-memory-card ${isMultiSelectMode && selectedMemories.includes(memory.id) ? 'selected' : ''}`}
              onClick={isMultiSelectMode ? () => toggleMemorySelection(memory.id) : undefined}
              style={isMultiSelectMode ? { cursor: 'pointer' } : {}}
            >
              <Card.Body>
                <div className="kb-memory-content">
                  {isMultiSelectMode && (
                    <div className="kb-checkbox-wrapper">
                      <Form.Check
                        type="checkbox"
                        checked={selectedMemories.includes(memory.id)}
                        onChange={() => toggleMemorySelection(memory.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="kb-item-checkbox"
                      />
                    </div>
                  )}
                  <div className="kb-memory-icon">
                    <i className="bi bi-lightbulb"></i>
                  </div>
                  <div className="kb-memory-text">
                    {expandedMemories[memory.id] ? (
                      <div className="kb-memory-main expanded sb-markdown-content">
                        <ReactMarkdown>{memory.memory}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="kb-memory-main">
                        {truncateText(memory.memory, 150)}
                      </p>
                    )}
                    {memory.memory?.length > 150 && (
                      <button 
                        className="sb-toggle-verbatim"
                        onClick={(e) => { e.stopPropagation(); toggleExpanded(memory.id); }}
                      >
                        <i className={`bi ${expandedMemories[memory.id] ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}></i>
                        {expandedMemories[memory.id] ? 'Show Less' : 'Read More'}
                      </button>
                    )}
                    <div className="kb-memory-meta">
                      <CategoryBadge category={memory.category} />
                      <ImportanceIndicator importance={memory.importance} />
                      <span className="kb-memory-date">
                        <i className="bi bi-calendar3 me-1"></i>
                        {formatDate(memory.metadata?.created_at)}
                      </span>
                    </div>
                    {/* Tags display */}
                    {memory.tags && memory.tags.length > 0 && (
                      <div className="kb-memory-tags">
                        {memory.tags.slice(0, 5).map((tag, i) => (
                          <span key={i} className="kb-tag">{tag}</span>
                        ))}
                        {memory.tags.length > 5 && (
                          <span className="kb-tag-more">+{memory.tags.length - 5}</span>
                        )}
                      </div>
                    )}
                  </div>
                  {!isMultiSelectMode && (
                    <div className="kb-memory-actions">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditClick(memory)}
                        title="Edit memory"
                        className="me-1"
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteClick(memory)}
                        title="Delete memory"
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {renderPagination()}

      {/* Refresh indicator */}
      {loading && memories.length > 0 && (
        <div className="kb-loading-overlay">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">Updating...</span>
        </div>
      )}

      {/* Add Memory Modal */}
      <AddMemoryModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddMemory}
      />

      {/* Edit Memory Modal */}
      <EditMemoryModal
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setMemoryToEdit(null);
        }}
        memory={memoryToEdit}
        onSave={handleEditSave}
      />

      {/* Delete Confirmation */}
      <ConfirmationPopup
        show={showDeleteConfirm}
        handleClose={() => {
          setShowDeleteConfirm(false);
          setMemoryToDelete(null);
        }}
        handleConfirm={handleDelete}
        title={selectedMemories.length > 0 ? `Delete ${selectedMemories.length} Memories?` : "Delete Memory?"}
        body={
          selectedMemories.length > 0
            ? `Are you sure you want to delete ${selectedMemories.length} selected memor${selectedMemories.length > 1 ? 'ies' : 'y'}? This action cannot be undone.`
            : memoryToDelete
              ? `Are you sure you want to delete this memory: "${memoryToDelete.memory?.substring(0, 50)}..."?`
              : "Are you sure you want to delete this memory?"
        }
        confirmText={deleting ? "Deleting..." : "Delete"}
        confirmVariant="danger"
      />
    </div>
  );
}
